/**
 * Consent, as a VALUE — what was agreed to, when, against which notice, and how
 * it is taken back.
 *
 * ── WHY A MODULE AND NOT A BOOLEAN ──────────────────────────────────────────
 *
 * The tempting shape is `consented: boolean` on the widget's state. It fails
 * three of the four things GDPR Art. 4(11) and Art. 7 ask of consent, and it
 * fails them silently:
 *
 *   • SPECIFIC. One flag covers every purpose at once, so a visitor who wanted
 *     the agent to see which page they are on has also agreed to be told where
 *     they arrived from. Art. 6(1)(a) is per purpose; a single flag cannot say
 *     which one. Hence {@link CONSENT_PURPOSES}, and hence the deliberate refusal
 *     to ship an "accept all" helper — the caller has to name the purpose.
 *   • INFORMED. A flag remembers the answer and not the QUESTION. Change the
 *     notice and yesterday's yes silently becomes a yes to a text that was never
 *     read. {@link NOTICE_VERSION} is on every decision, and a decision recorded
 *     against a different version is treated as NO DECISION AT ALL — not as a
 *     grant, and not as a refusal either, so the visitor is asked again rather
 *     than being both re-prompted and counted as having agreed.
 *   • REVOCABLE, "as easy to withdraw as to give" (Art. 7(3)). A flag has no
 *     revocation, only a second write that looks exactly like the first, so
 *     nothing in the code distinguishes "took it back" from "changed their
 *     mind about giving it". {@link revoke} is a named function, one argument,
 *     the same effort as {@link grant}, and it takes effect on the next call to
 *     {@link isGranted} — there is no cache to invalidate because this module
 *     holds no cache.
 *
 * The fourth, FREELY GIVEN, is not something a type can hold. What this module
 * can do is refuse the shapes that make it untrue, and it does exactly one: the
 * absence of a decision is NOT a grant. There is no opt-out default and no
 * `defaultGranted` parameter. Silence, a closed banner, a scrolled page and a
 * pre-ticked box are all the same value here — no decision — and
 * {@link isGranted} answers false for all of them.
 *
 * ── WHAT THIS FILE DELIBERATELY DOES NOT DO ─────────────────────────────────
 *
 * NO STORAGE. It does not read or write `sessionStorage`, `localStorage`, a
 * cookie or the network. Two reasons, and the second is the load-bearing one:
 *
 *   1. The unit suite for this app runs on a hermetic node runner
 *      (`packages/config-vitest/src/index.js:51`, `environment: 'node'`), so a
 *      module that touches `window` at import time is a module whose consent
 *      rules cannot be tested at all.
 *   2. WHERE a consent record lives is a decision with its own cost, and it
 *      belongs to the caller that already owns the other two records this app
 *      keeps. Both of those — `api/session-store.ts` and `app/seen.ts` — argue
 *      for `sessionStorage` over `localStorage` in writing, and the argument
 *      applies here unchanged and one step harder: a widget audience is
 *      frequently on a shared or public machine, and a durable record of what a
 *      named visitor agreed to is worse to leave behind than a read pointer.
 *      {@link serializeConsent} and {@link parseConsent} exist so that caller can
 *      mirror the value into `sessionStorage` on the WIDGET origin without this
 *      module deciding for it. `localStorage` is the wrong answer for a second
 *      reason as well: a value that survives the tab on our own origin, keyed to
 *      nothing else, is one field away from being the cross-site identifier
 *      `privacy/visitor-context.ts` refuses by name.
 *
 * NO SERVER RECORD. `consent_record` exists (03_identity.prisma:293) with
 * `source = 'widget'` already in its CHECK, and it is NOT written from here.
 * That is a gap and not a design: no widget route accepts a consent decision —
 * `packages/contracts/src/routes/widget.ts` declares four routes
 * (`widget.session.create`, `widget.conversation.get`, `widget.messages.send`,
 * `widget.stream.open`) and none of them has a field for one. Its `purpose`
 * CHECK is `transactional|marketing|research`, which does not contain either
 * purpose below, so wiring it is a contract change plus a CHECK change plus a
 * migration — all three outside this task. Until that exists, a revocation is
 * effective LOCALLY and IMMEDIATELY, which is what the visitor can observe, and
 * nothing has been sent that a server would have to forget.
 *
 * WHAT BREAKS IF THIS DESIGN CHANGES. Add a default-true purpose, or make
 * {@link isGranted} fall back to true when a decision is missing or malformed,
 * and every optional field in `visitor-context.ts` starts being collected from
 * visitors who never agreed to it — with no error, no log and no visible symptom
 * on either side of the conversation. That failure mode is why {@link parseConsent}
 * discards what it cannot fully verify instead of repairing it.
 */

/**
 * The purposes a visitor can be asked about, and nothing else can be asked.
 *
 * Closed on purpose, and short on purpose. Each entry below has to survive the
 * same question `visitor-context.ts` asks of its necessary fields — "what does
 * the agent do with this that they cannot do without it?" — and then FAIL it,
 * because a purpose that passes belongs in the necessary set where no consent is
 * needed and nothing can be refused.
 *
 * `page-context` — WHICH PAGE the visitor is on, as a path. The agent's first
 * question is almost always "where are you seeing this?", and a path answers it
 * without a round trip. It is not necessary: the visitor can type it, and the
 * page ORIGIN (which the agent does get without consent) already narrows it to
 * one of the brand's sites. It is separated from the necessary set because a
 * path can carry an identifier a query string would have carried — `/orders/8814`,
 * `/u/ada` — and `visitor-context.ts` can strip a query string but cannot know
 * that a path segment is somebody's order number.
 *
 * `arrival-context` — the ORIGIN the visitor arrived from. Useful when the
 * question is about a campaign or a partner site the brand does not control. It
 * is not necessary by a wide margin, and it is the most sensitive value either
 * module handles: a referrer is a statement about where this person was BEFORE
 * this site, which is the cross-site browsing history that
 * `visitor-context.ts`'s refusals exist to keep out. It is offered at all only
 * reduced to a bare origin, and only behind its own separate consent, so that a
 * visitor can say yes to the page they are on and no to where they came from.
 * Those two answers being separable is the entire reason there are two purposes
 * and not one.
 */
export const CONSENT_PURPOSES = ['page-context', 'arrival-context'] as const;

export type ConsentPurpose = (typeof CONSENT_PURPOSES)[number];

/**
 * Which text the visitor was shown when they answered.
 *
 * BUMP THIS whenever the notice's meaning changes — a new purpose, a wider
 * field, a different recipient. Every decision carried against an older version
 * stops counting the moment this constant moves, in both directions: an old
 * grant is not a grant, and an old refusal is not a refusal, so the visitor is
 * asked once more against the text that is now true.
 *
 * The cost is named rather than hidden: a bump re-prompts every visitor who had
 * already answered, including the ones who said no, which is mildly annoying and
 * is the correct trade against consent to a sentence nobody read. A version that
 * is NOT bumped after a notice change is the failure this constant exists to
 * make visible, and no gate can catch it — it is a human obligation, written
 * here so the next person editing the notice sees it.
 */
export const NOTICE_VERSION = 1;

/** One purpose, one answer, with the two facts that make the answer meaningful. */
export interface ConsentDecision {
  readonly purpose: ConsentPurpose;
  /** false is a real, recorded answer and is NOT the same as no answer at all. */
  readonly granted: boolean;
  /** ISO-8601, from the caller's clock. Never `Date.now()` inside this module. */
  readonly decidedAt: string;
  readonly noticeVersion: number;
}

/**
 * Every decision this visitor has made, at most one per purpose.
 *
 * A frozen array rather than a record keyed by purpose: it serialises to
 * something a human can read in a bug report, and it survives a purpose being
 * renamed — {@link parseConsent} drops the row it no longer recognises instead of
 * carrying an orphan key that some later `Object.keys` walk would treat as a
 * grant.
 */
export interface ConsentState {
  readonly decisions: readonly ConsentDecision[];
}

const EMPTY: ConsentState = Object.freeze({ decisions: Object.freeze([]) });

/**
 * The starting value, and the value a refusal to store anything degrades to.
 *
 * It is also what {@link forgetConsent} returns, because "erase what you hold
 * about me" and "we have never asked you" are the same state as far as this
 * module is concerned — the record of a past answer is itself a record about a
 * person, so an erasure that kept the consent log would be keeping the one thing
 * the visitor was most explicitly done with.
 */
export function noConsent(): ConsentState {
  return EMPTY;
}

function isPurpose(value: unknown): value is ConsentPurpose {
  return typeof value === 'string' && (CONSENT_PURPOSES as readonly string[]).includes(value);
}

/**
 * Replace this purpose's answer, leaving every other purpose untouched.
 *
 * Replace and not append: two answers for one purpose means the reader has to
 * decide which wins, and "the last one" is only correct while nothing ever
 * merges two states or restores one out of order. One row per purpose makes that
 * question unaskable.
 */
function decide(
  state: ConsentState,
  purpose: ConsentPurpose,
  granted: boolean,
  decidedAt: Date,
): ConsentState {
  const kept = state.decisions.filter((decision) => decision.purpose !== purpose);
  return Object.freeze({
    decisions: Object.freeze([
      ...kept,
      Object.freeze({
        purpose,
        granted,
        decidedAt: decidedAt.toISOString(),
        noticeVersion: NOTICE_VERSION,
      }),
    ]),
  });
}

/**
 * The visitor said yes to ONE purpose, at a moment the caller names.
 *
 * `decidedAt` is a parameter and has no default. A consent record whose timestamp
 * came from inside the function is a record nobody can test and nobody can
 * reproduce in a support conversation; passing it makes "when did I agree to
 * this?" answerable and makes this module clock-free.
 */
export function grant(state: ConsentState, purpose: ConsentPurpose, decidedAt: Date): ConsentState {
  return decide(state, purpose, true, decidedAt);
}

/**
 * The visitor took it back, or refused in the first place.
 *
 * ONE ARGUMENT MORE THAN {@link grant} NEEDS, WHICH IS ZERO — that symmetry is
 * the Art. 7(3) requirement expressed in the only place a reviewer will look.
 * There is deliberately no `revokeAll`, for the same reason there is no
 * `grantAll`: a bulk control on the withdrawal side and none on the granting
 * side would be the same asymmetry pointed the other way, and it is the shape a
 * later "simplification" is most likely to add.
 *
 * The effect is immediate because there is nothing to invalidate: this returns a
 * new value and {@link isGranted} recomputes from it. A caller that has cached a
 * built context must rebuild it — `visitor-context.ts`'s test suite asserts that
 * rebuild drops the field, which is the defect this pair would otherwise hide.
 */
export function revoke(
  state: ConsentState,
  purpose: ConsentPurpose,
  decidedAt: Date,
): ConsentState {
  return decide(state, purpose, false, decidedAt);
}

/**
 * Everything is forgotten: the answers and the fact that they were ever asked.
 *
 * The visitor-facing half of erasure for the only personal data this app holds
 * by itself. It is local and instant, which is what the visitor can verify; the
 * server side of the same request is `POST /v1/people/{personId}/erasure`, which
 * a visitor cannot call — see `visitor-context.ts` for that gap in full.
 */
export function forgetConsent(): ConsentState {
  return EMPTY;
}

function find(state: ConsentState, purpose: ConsentPurpose): ConsentDecision | null {
  // A decision recorded against a different notice is not this notice's
  // decision. Filtering here rather than at every call site is what makes
  // "informed" a property of the module instead of a habit of its callers.
  return (
    state.decisions.find(
      (decision) => decision.purpose === purpose && decision.noticeVersion === NOTICE_VERSION,
    ) ?? null
  );
}

/**
 * May this purpose's data be collected right now?
 *
 * The ONLY question `visitor-context.ts` is allowed to ask. It answers false for
 * every uncertainty there is — never asked, refused, asked against an older
 * notice, a state that came back malformed from storage — because each of those
 * is a case where nobody can point at the moment this visitor agreed.
 */
export function isGranted(state: ConsentState, purpose: ConsentPurpose): boolean {
  return find(state, purpose)?.granted === true;
}

/**
 * Has this visitor answered this question, under the notice that is current?
 *
 * Separate from {@link isGranted} because the UI needs three states and not two:
 * ask, do not ask (they said yes), do not ask (they said no). Collapsing refused
 * into unasked produces a banner that returns on every page of the customer's
 * site until the visitor gives in, which is the definition of consent that is
 * not freely given.
 */
export function wasAsked(state: ConsentState, purpose: ConsentPurpose): boolean {
  return find(state, purpose) !== null;
}

/**
 * What the visitor may be shown as "what you have agreed to".
 *
 * Ordered by {@link CONSENT_PURPOSES} rather than by decision order, so the list
 * a visitor reads is stable between visits and does not reshuffle when they
 * change one answer.
 */
export function grantedPurposes(state: ConsentState): readonly ConsentPurpose[] {
  return CONSENT_PURPOSES.filter((purpose) => isGranted(state, purpose));
}

/** For a caller that mirrors this into `sessionStorage`. See the header. */
export function serializeConsent(state: ConsentState): string {
  return JSON.stringify(state);
}

/**
 * Read back what was mirrored, TRUSTING NOTHING.
 *
 * Same posture as `api/session-store.ts`'s `parseCached` and for a sharper
 * reason: that one is guarding against a shape change across a deploy, and this
 * one is also guarding against a hand-edited storage key being read as consent
 * the visitor never gave. Anything that is not a fully-formed decision is
 * DISCARDED rather than defaulted, per row, so one bad entry cannot take the
 * visitor's other answers with it and cannot become a grant.
 *
 * Discarding a row makes the purpose unasked, so the visitor is asked again. The
 * cost of the opposite choice — repairing a partial row — is a grant with no
 * verifiable origin, which is exactly what an audit of consent has to be able to
 * rule out.
 */
export function parseConsent(raw: string): ConsentState {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch (notJson) {
    if (notJson instanceof SyntaxError) {
      return EMPTY;
    }
    throw notJson;
  }

  if (typeof value !== 'object' || value === null) {
    return EMPTY;
  }
  const rows: unknown = (value as Record<string, unknown>)['decisions'];
  if (!Array.isArray(rows)) {
    return EMPTY;
  }

  const decisions: ConsentDecision[] = [];
  const seen = new Set<ConsentPurpose>();
  for (const row of rows as readonly unknown[]) {
    if (typeof row !== 'object' || row === null) {
      continue;
    }
    const candidate = row as Record<string, unknown>;
    const { purpose, granted, decidedAt, noticeVersion } = candidate;
    if (
      !isPurpose(purpose) ||
      // `=== true`/`=== false` and not `typeof granted === 'boolean'`: the string
      // "false" and the number 0 must not become a boolean by way of a coercion
      // somebody adds later, and this spelling has nowhere to put one.
      (granted !== true && granted !== false) ||
      typeof decidedAt !== 'string' ||
      typeof noticeVersion !== 'number' ||
      // A duplicate purpose means the record was written by something that is not
      // `decide`. The FIRST row wins and the rest are dropped, rather than the
      // last, because "last wins" would let an appended row overwrite an answer.
      seen.has(purpose)
    ) {
      continue;
    }
    // Not a validator for the CONTENT of the timestamp — only that it is a
    // parseable instant. A record whose date is unreadable cannot answer "when
    // did I agree to this?", and a consent record that cannot answer that is not
    // evidence of anything.
    if (Number.isNaN(Date.parse(decidedAt))) {
      continue;
    }
    seen.add(purpose);
    decisions.push(Object.freeze({ purpose, granted, decidedAt, noticeVersion }));
  }

  return Object.freeze({ decisions: Object.freeze(decisions) });
}
